import { Box, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { DataGrid } from '@verantos-dev/shared-components';

import type { MappingResult } from '../types';
import { MappingStatus, type MappingResultRow } from './useMappingResultsColumns';

/**
 * Custom hook to manage DataGrid configuration for mapping results
 *
 * This hook encapsulates all the DataGrid-specific logic including:
 * - Row data preparation with unique IDs
 * - Detail panel (expandable rows) content and height
 * - Row and cell className logic for error styling
 *
 * FEATURES:
 * - Transforms results into DataGrid-compatible rows
 * - Handles expandable rows with nested DataGrid for mapped concepts
 * - Applies error styling for unmapped/not found items
 * - Memoized for optimal performance
 *
 * USAGE:
 * ```tsx
 * const columns = useMappingResultsColumns();
 * const dataGridConfig = useMappingResultsDataGrid(results, columns);
 *
 * <DataGrid
 *   rows={dataGridConfig.rows}
 *   columns={columns}
 *   getDetailPanelContent={dataGridConfig.getDetailPanelContent}
 *   getDetailPanelHeight={dataGridConfig.getDetailPanelHeight}
 *   getRowClassName={dataGridConfig.getRowClassName}
 *   getCellClassName={dataGridConfig.getCellClassName}
 *   ...
 * />
 * ```
 */

// No-op function for client-mode DataGrid
const noOpFetch = () => {
  // This is intentionally empty for client-mode DataGrid
};

export interface UseMappingResultsDataGridReturn {
  /** Prepared rows with unique rowId for DataGrid */
  rows: MappingResultRow[];

  /** Function to render detail panel content for expandable rows */
  getDetailPanelContent: (params: { row: MappingResultRow }) => React.ReactNode;

  /** Function to calculate detail panel height */
  getDetailPanelHeight: (params: { row: MappingResultRow }) => number;

  /** Function to determine row className for styling */
  getRowClassName: (params: { row: MappingResultRow }) => string;

  /** Function to determine cell className for styling */
  getCellClassName: (params: { row: MappingResultRow }) => string;
}

export const useMappingResultsDataGrid = (
  results: MappingResult[],
  columns: GridColDef<MappingResultRow>[]
): UseMappingResultsDataGridReturn => {
  const theme = useTheme();

  // ============================================================================
  // ROW PREPARATION
  // ============================================================================

  /**
   * Helper function to get sort order based on status
   * Primary sort by status: Not found (0), Unmapped (1), Mapped (2), Already standard (3)
   */
  const getStatusOrder = (status: string): number => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'not found') return 0;
    if (statusLower === 'unmapped') return 1;
    if (
      statusLower === 'mapped' ||
      statusLower === 'one mapping' ||
      statusLower === 'multiple mappings' ||
      statusLower === 'multiple mapping'
    )
      return 2;
    if (statusLower === 'already standard') return 3;
    return 4; // Unknown status goes to end
  };

  /**
   * Transform results into DataGrid rows with unique IDs
   * Only includes parent rows (not nested mapped children)
   * Sorted by status first, then by number of nested mapped rows
   */
  const rows: MappingResultRow[] = useMemo(() => {
    return results
      .map((result, index) => ({
        ...result,
        // Use a combination of index and id for unique row ID
        rowId: `${index}-${result.id}`,
      }))
      .sort((a, b) => {
        // First sort by status
        const statusOrderA = getStatusOrder(a.status || '');
        const statusOrderB = getStatusOrder(b.status || '');

        if (statusOrderA !== statusOrderB) {
          return statusOrderA - statusOrderB;
        }

        // If status is "Mapped" or similar, sort by number of nested mapped rows
        // Rows with multiple mapped rows (count > 1) should come before those with single mapped row (count === 1)
        if (statusOrderA === 2) {
          // Status is "Mapped"
          const countA = a.isCollapsible && a.mappedRows ? a.mappedRows.length : 0;
          const countB = b.isCollapsible && b.mappedRows ? b.mappedRows.length : 0;

          // Multiple mappings (count > 1) should come before single mappings (count === 1)
          if (countA > 1 && countB === 1) return -1;
          if (countA === 1 && countB > 1) return 1;

          // Within same count, sort by count descending
          return countB - countA;
        }

        return 0;
      });
  }, [results]);

  // ============================================================================
  // DETAIL PANEL CONTENT (Expandable Rows)
  // ============================================================================

  /**
   * Renders the detail panel content for expandable rows
   * Shows a nested DataGrid with the mapped concepts
   */
  const getDetailPanelContent = useMemo(
    () => (params: { row: MappingResultRow }) => {
      // Only show detail panel for collapsible rows
      if (!params.row.isCollapsible) {
        return null;
      }

      const mappedRows = params.row.mappedRows;
      if (!mappedRows || mappedRows.length === 0) {
        return null;
      }

      // Calculate exact height for the nested DataGrid: rows (52px each)
      const nestedGridHeight = mappedRows.length * 52;

      // Create columns with a dummy first column to match the toggle column width
      const nestedColumns = [
        {
          field: '__dummy__',
          headerName: '',
          width: 50,
          sortable: false,
          filterable: false,
          hideable: false,
          resizable: false,
          renderCell: () => null,
          renderHeader: () => null,
        },
        ...columns,
      ];

      return (
        <Box
          sx={{
            bgcolor: theme.palette.grey[50],
            py: 0,
            pl: 0,
            pr: 0,
            height: `${nestedGridHeight}px`,
            overflow: 'hidden',
            width: '100%', // Ensure full width to match parent DataGrid
          }}
        >
          <DataGrid
            id={`detail-${params.row.rowId}`}
            mode="client"
            rows={mappedRows.map((row, idx) => ({
              ...row,
              rowId: `${params.row.rowId}-child-${idx}`,
              status: '', // Remove status for nested mapped rows
            }))}
            columns={nestedColumns}
            getRowId={(row) => row.rowId}
            useFetchDataGridData={noOpFetch}
            hideFooter
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableColumnResize
            getRowClassName={(params) => {
              const classes = [];
              const status = params.row.status?.toString().trim() || '';
              if (status === MappingStatus.UNMAPPED || status === MappingStatus.NOT_FOUND) {
                classes.push('error-row');
              }
              if (!params.row.isCollapsible) {
                classes.push('non-collapsible');
              }
              return classes.join(' ');
            }}
            sx={{
              border: 'none',
              height: `${nestedGridHeight}px`,
              width: '100%',
              '& .MuiDataGrid-main': {
                height: `${nestedGridHeight}px !important`,
                width: '100% !important',
              },
              '& .MuiDataGrid-virtualScroller': {
                height: `${nestedGridHeight}px !important`,
                overflow: 'hidden !important',
                width: '100% !important',
              },
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="__dummy__"]': {
                padding: 0,
                border: 'none',
                minWidth: '50px !important',
                maxWidth: '50px !important',
                width: '50px !important',
              },
              '& .MuiDataGrid-row.error-row': {
                backgroundColor: `${theme.palette.error.light} !important`,
                '&:hover': {
                  backgroundColor: `${theme.palette.error.main} !important`,
                },
              },
              '& .MuiDataGrid-cell': {
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              // Ensure the nested DataGrid has the same column widths as parent
              '& .MuiDataGrid-root': {
                width: '100%',
              },
              '& .MuiDataGrid-container--top': {
                width: '100%',
              },
            }}
          />
        </Box>
      );
    },
    [columns, theme]
  );

  // ============================================================================
  // DETAIL PANEL HEIGHT
  // ============================================================================

  /**
   * Calculates the height for detail panel based on number of mapped rows
   * Returns 0 for non-collapsible rows (CSS will hide the expand icon)
   */
  const getDetailPanelHeight = useMemo(
    () => (params: { row: MappingResultRow }) => {
      if (!params.row.isCollapsible) return 0;

      const mappedRows = params.row.mappedRows;
      if (!mappedRows || mappedRows.length === 0) return 0;

      // Height calculation: rows (52px each) - exact height to match nested DataGrid
      // Note: header is hidden so we don't include its height
      return mappedRows.length * 52;
    },
    []
  );

  // ============================================================================
  // ROW CLASSNAME (Styling Logic)
  // ============================================================================

  /**
   * Determines CSS className for rows
   * - 'error-row' highlights an unmapped / not-found source row in red, so a
   *   code that won't contribute a standard concept is visually obvious (the
   *   nested mapped-rows grid already does this; the parent did not).
   * - 'non-collapsible' for rows that shouldn't be expandable.
   */
  const getRowClassName = useMemo(
    () => (params: { row: MappingResultRow }) => {
      const classes = [];
      const status = params.row.status?.toString().trim() || '';
      if (status === MappingStatus.UNMAPPED || status === MappingStatus.NOT_FOUND) {
        classes.push('error-row');
      }
      if (!params.row.isCollapsible) {
        classes.push('non-collapsible');
      }
      return classes.join(' ');
    },
    []
  );

  // ============================================================================
  // CELL CLASSNAME (Error Styling)
  // ============================================================================

  /**
   * Determines CSS className for cells
   * Adds 'error-cell' class to cells in unmapped/not found rows
   */
  const getCellClassName = useMemo(
    () => (params: { row: MappingResultRow }) => {
      const status = params.row.status?.toString().trim() || '';
      if (status === 'Unmapped' || status === 'Not found') {
        return 'error-cell';
      }
      return '';
    },
    []
  );

  // ============================================================================
  // RETURN HOOK INTERFACE
  // ============================================================================

  return {
    rows,
    getDetailPanelContent,
    getDetailPanelHeight,
    getRowClassName,
    getCellClassName,
  };
};
